import request from 'api/requestUtils'
import { baseUri, getOptions } from './utils'
import type { OfferDetailBatchResponse, OfferDetailBatchResult } from './types'

const endpointUri = `${baseUri}/v2/offers/batch`

/**
 * Note the absent `offerTypes`. The contract documents it as an optimisation ("omit entirely
 * to probe every offer's type"), and it has been suggested as an obvious win in review twice.
 *
 * **Do not send it for bedbank offers.** `resolveAccommodationPropertyId` returns early for any
 * type that is not `bedbank_hotel`/`tactical_ao_hotel`, so with no types sent every request
 * lands in svc-trip's discovery branch. Send them, and a `bedbank_hotel` card resolves a
 * property id while `sppPricingPropertyId` stays undefined (that is set only for
 * `tactical_ao_hotel`) — which routes it down the all-SPP path and drops ~19 fields, including
 * `hasPromotions`, flipping the card to `walledGarden: true` and the wrong product type.
 *
 * If it is worth doing: send types only for `hotel`/`tactical_ao_hotel` and pad bedbank with an
 * empty segment — svc-trip's schema supports that deliberately. Until then the carousel's field
 * completeness rests on this omission rather than on a guarantee (review #37389 [5/50]).
 */
export interface GetOfferDetailBatchParams {
  offerIds: Array<string>
  region: string
  checkIn?: string
  checkOut?: string
  occupancies?: string
}

export function getOfferDetailBatch(
  params: GetOfferDetailBatchParams,
): Promise<OfferDetailBatchResult> {
  const queryParams = new URLSearchParams()
  queryParams.set('offerIds', params.offerIds.join(','))
  queryParams.set('region', params.region)
  if (params.checkIn) queryParams.set('checkIn', params.checkIn)
  if (params.checkOut) queryParams.set('checkOut', params.checkOut)
  if (params.occupancies) queryParams.set('occupancies', params.occupancies)
  const uri = `${endpointUri}?${queryParams.toString()}`
  return request
    .get<OfferDetailBatchResponse>(uri, getOptions())
    .then((response) => response.result)
}
